import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
} from "@mui/material";
import SoupKitchenRoundedIcon from "@mui/icons-material/SoupKitchenRounded";
import AirRoundedIcon from "@mui/icons-material/AirRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import LunchDiningRoundedIcon from "@mui/icons-material/LunchDiningRounded";
import { useDispatch, useSelector } from "react-redux";
import { setQueryError } from "./searchQuery/searchQuerySlice";
import SearchInput from "./searchQuery/components/SearchInput";
import IngredientInput from "./ingredientsInput/components/IngredientInput";
import IngredientList from "./ingredientsList/components/IngredientList";
import {
  resetSearchState,
  updateIngredientsAndFilter,
} from "./searchResults/searchResultsSlice";
import { fetchRecipes } from "./searchResults/searchResultsThunk";

const heroTags = ["Sunny flavors", "Fresh ingredients", "Kitchen magic"];
const steamPositions = [
  { top: 18, left: 22, delay: "0s" },
  { top: 6, left: 46, delay: "0.8s" },
  { top: 16, left: 68, delay: "1.6s" },
];

const SearchForm = () => {
  const dispatch = useDispatch();
  const { query } = useSelector((state) => state.searchQuery);
  const { ingredients } = useSelector((state) => state.ingredientsList);

  const isValidInput = (text) => /^[a-zA-Z\s-]+$/.test(text.trim());

  const handleSubmit = async () => {
    if (!query.trim()) {
      dispatch(setQueryError("Search keyword is required"));
      return;
    }

    if (!isValidInput(query)) {
      dispatch(
        setQueryError(
          "Invalid input: input can only contain letters, spaces, and hyphens"
        )
      );
      return;
    }

    dispatch(resetSearchState());
    const result = dispatch(fetchRecipes({ query, ingredients }));

    if (fetchRecipes.fulfilled.match(result)) {
      dispatch(updateIngredientsAndFilter(ingredients));
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        maxWidth: 940,
        mx: "auto",
        mt: { xs: 1, md: 3 },
        mb: 6,
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 5 },
        borderRadius: 3,
        background:
          "linear-gradient(135deg, rgba(255,252,244,0.78), rgba(255,247,237,0.7) 48%, rgba(224,242,254,0.62) 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -80,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(14,165,233,0.28), transparent 65%)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -70,
          left: -55,
          width: 230,
          height: 230,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(250,204,21,0.3), transparent 65%)",
        }}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 4, md: 5 }}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Box sx={{ flex: 1.1, position: "relative" }}>
          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {heroTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                sx={{
                  bgcolor: "rgba(255,255,255,0.7)",
                  color: "secondary.dark",
                  fontWeight: 700,
                  backdropFilter: "blur(4px)",
                }}
              />
            ))}
          </Stack>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.75rem" },
              lineHeight: 0.95,
              color: "text.primary",
              maxWidth: 460,
            }}
          >
            Let&apos;s Get Cooking
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mt: 2,
              maxWidth: 470,
              color: "text.secondary",
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            Stir up bright recipe ideas with juicy ingredients, sunny colors,
            and a kitchen full of delicious inspiration.
          </Typography>

          <Typography
            variant="overline"
            sx={{
              display: "block",
              mt: 1.5,
              color: "primary.dark",
              letterSpacing: "0.18em",
              fontWeight: 800,
            }}
          >
            Recipe Search Reimagined
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 3, color: "primary.main" }}>
            <RestaurantRoundedIcon sx={{ fontSize: 32 }} />
            <LunchDiningRoundedIcon sx={{ fontSize: 32, color: "secondary.main" }} />
            <AirRoundedIcon sx={{ fontSize: 32, color: "#facc15" }} />
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 0.9,
            position: "relative",
            minHeight: { xs: 150, md: 260 },
            display: { xs: "none", sm: "block" },
          }}
        >
          {steamPositions.map((steam, index) => (
            <AirRoundedIcon
              key={index}
              sx={{
                position: "absolute",
                top: steam.top,
                left: `${steam.left}%`,
                fontSize: 42,
                color: "rgba(14,165,233,0.36)",
                animation: "steamDrift 3.4s ease-in-out infinite",
                animationDelay: steam.delay,
              }}
            />
          ))}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: { xs: 170, md: 240 },
                height: { xs: 170, md: 240 },
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at 30% 30%, rgba(254,240,138,0.9), rgba(249,115,22,0.85) 60%, rgba(234,88,12,0.92) 100%)",
                boxShadow: "0 28px 55px rgba(249, 115, 22, 0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SoupKitchenRoundedIcon
                sx={{
                  fontSize: { xs: 92, md: 128 },
                  color: "#fff7ed",
                  filter: "drop-shadow(0 10px 18px rgba(124,45,18,0.25))",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Stack>

      <Box
        sx={{
          mt: 5,
          pt: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 0.75, color: "text.primary", fontWeight: 800 }}
        >
          Build your perfect meal idea
        </Typography>
        <Typography sx={{ mb: 3, color: "text.secondary" }}>
          Search by cuisine or dish, then sprinkle in ingredients you want to
          cook with today.
        </Typography>

        <SearchInput />
        <IngredientInput />
        <IngredientList />
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{
            mt: 1.5,
            py: 1.5,
            borderRadius: 2,
            fontSize: "1.05rem",
            background: "linear-gradient(135deg, #f97316, #fb923c)",
            boxShadow: "0 14px 30px rgba(249, 115, 22, 0.32)",
            "&:hover": {
              background: "linear-gradient(135deg, #ea580c, #f97316)",
            },
          }}
        >
          Search
        </Button>
      </Box>
    </Box>
  );
};

export default SearchForm;
